import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Rendered instead of the children when they throw. Tiles pass a broken-tile card. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * §5.3: "a tile that throws shows a broken-tile card and never takes the page down."
 * The same boundary wraps the app shell, so one bad route cannot blank the SPA.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Phase 8 swaps this for the error tracker; the hook point exists now so nothing
    // has to be re-plumbed later.
    reportError(Object.assign(error, { componentStack: info.componentStack }));
  }

  private reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <div className="panel panel--error" role="alert">
        <h2>Something broke</h2>
        <p>{error.message}</p>
        <button type="button" onClick={this.reset}>
          Try again
        </button>
      </div>
    );
  }
}
